import Config from "../constants/Config.js";
import keyValueToURLParams from "../utils/keyValueToURLParams";
import { getCategoryForPostType, getPostTypeAPIName } from "../utils/post.js";
import { BreadcrumbLinkType } from "../types";
import qs from "query-string";
import Store from "../store";
import ActionTypes from "../constants/ActionTypes";
import { getProfile } from "../actions/user";

let invalidNonceCount = 0;

export const wordpressFetch = (
    method: string = "GET",
    url: string,
    headers?: Headers | null,
    body?: {} | null,
    returnHeaders: boolean | null = false,
    dontStringify: boolean | null = false
): Promise<any> => {
    const isPrivate =
        url.startsWith(Config.dynamicPageAPI) ||
        (url.startsWith(Config.shopAPI) &&
            !url.startsWith(`${Config.shopAPI}/products`)) ||
        url.startsWith(Config.userAPI) ||
        url.startsWith(Config.lambdaUserAPI) ||
        url.startsWith(`${Config.formAPI}/file-upload`);
    return innerFetch(
        {
            method,
            url,
            headers,
            body,
            returnHeaders,
            dontStringify,
        },
        isPrivate
    );
};

const innerFetch = async (
    inputArgs: {
        method?: string;
        url: string;
        headers?: Headers | null;
        body?: {} | null;
        returnHeaders: boolean | null;
        dontStringify: boolean | null;
    },
    nonceRequired = false,
    getNewNonce?: boolean,
    fetchAttempts = 0
) => {
    const fetchArgs = {
        ...{
            method: "GET",
            returnHeaders: false,
            dontStringify: false,
        },
        ...inputArgs,
    };

    let finalHeaders;
    if (fetchArgs.headers) {
        finalHeaders = fetchArgs.headers;
    } else {
        finalHeaders = new Headers();
        finalHeaders.append("Content-Type", "application/json");
    }

    if (nonceRequired) {
        const nonceResponse = await getUserNonce(getNewNonce);

        if (nonceResponse.nonce) {
            finalHeaders.append("X-WP-Nonce", nonceResponse.nonce);
        }
    }

    const requestOptions: Object = {
        method: fetchArgs.method,
        body: fetchArgs.body
            ? fetchArgs.dontStringify
                ? fetchArgs.body
                : JSON.stringify(fetchArgs.body)
            : null,
        credentials: process.env.REACT_APP_CORS ? "same-origin" : "include",
        headers: finalHeaders,
    };

    try {
        const res = await fetch(fetchArgs.url, requestOptions);
        if (
            (res.status >= 500 && res.status < 600) ||
            res.status === 429 ||
            res.status === 403 ||
            res.status === 400 ||
            res.status === 401 ||
            res.status === 409
        ) {
            throw res;
        }
        const responseJson = await res.json();

        // special handling for 301 redirects
        if (responseJson.status === 301 && responseJson.location) {
            window.location.replace(responseJson.location);
        }

        // set headers object from response headers
        let headers = {};
        for (let [key, value] of res.headers.entries()) {
            headers[key] = value;
        }

        const responseJsonWithHeaders = fetchArgs.returnHeaders
            ? {
                  data: responseJson,
                  headers,
              }
            : responseJson;

        return responseJsonWithHeaders;
    } catch (err: any) {
        if (err.status === 403 && fetchAttempts < 3) {
            // Check if this is an invalid nonce error — the WP cookie is stale
            // and retrying won't help. Redirect to login so the user gets a
            // fresh session.
            if (typeof err.json === "function") {
                const errBody = await err.clone().json().catch(() => null);
                if (errBody?.code === "rest_cookie_invalid_nonce") {
                    invalidNonceCount++;
                    if (invalidNonceCount >= 2) {
                        invalidNonceCount = 0;
                        // Clear stale WP cookies so the login page starts fresh
                        document.cookie.split(";").forEach(c => {
                            const name = c.split("=")[0].trim();
                            if (name.startsWith("wordpress_logged_in_")) {
                                document.cookie = `${name}=; expires=${new Date(0).toUTCString()}; path=/`;
                            }
                        });
                        window.location.href = "/my-account/login";
                        return;
                    }
                }
            }
            // unauthorized, attempt to refetch with new nonce
            return Store.dispatch(getProfile(true)).then(() => {
                return innerFetch(
                    inputArgs,
                    nonceRequired,
                    getNewNonce,
                    fetchAttempts + 1
                );
            });
            // return innerFetch(true);
        } else if (err.status === 304) {
            throw err;
        } else if (typeof err.json === "function") {
            return err
                .json()
                .catch(() => {
                    throw new Error("Unknown error"); // error response is not json
                })
                .then(json => {
                    throw Object({
                        ...json,
                        message: json.message || "Unknown error", // error response is json, maybe doesn't have message
                        httpStatus: err.status || 999,
                    });
                });
        } else if (err.message) {
            throw err; // an error object, possible timeout error
        }
        throw new Error("Unknown error"); // unknow error
    }
};

export const getUserNonce = (forceNewNonce?: boolean): Promise<any> => {
    return new Promise((resolve: any, reject: any): any => {
        const { user } = Store.getState();
        const existingNonce =
            user.nonce && user.nonce.nonce ? user.nonce.nonce : null;

        if (!forceNewNonce && existingNonce) {
            resolve(user.nonce);
        } else {
            fetch(Config.nonceAPI, {
                credentials: process.env.REACT_APP_CORS
                    ? "same-origin"
                    : "include",
            })
                .then((res: any): any => res.json())
                .then(nonce => {
                    resolve(nonce);

                    Store.dispatch({
                        type: ActionTypes.SET_USER_NONCE,
                        nonce,
                    });
                })
                .catch(() => {
                    resolve(null);
                });
        }
    });
};

export const fetchTagsCounter = (): Promise<any> => {
    const tagsCounterPromise = new Promise(
        (resolve: Function, reject: Function) => {
            try {
                let serializedState = localStorage.getItem(
                    Config.tagsCounterKey
                );
                if (serializedState === null) {
                    localStorage.setItem(
                        Config.tagsCounterKey,
                        JSON.stringify({})
                    );
                    resolve({});
                } else if (serializedState === undefined) {
                    resolve({});
                } else {
                    resolve(JSON.parse(serializedState || ""));
                }
            } catch (err) {
                //If user disabled LocalStorage, still resolve so that TagsCounter at least lives during runtie
                resolve({});
            }
        }
    );
    return tagsCounterPromise;
};

export const fetchContentTags = (): Promise<any> => {
    return wordpressFetch("GET", Config.tagsAPI);
};

export const fetchEvents = (): Promise<any> => {
    return wordpressFetch("GET", `${Config.eventsApi}?orderby=featured`);
};

export const fetchClubs = (): Promise<any> => {
    return wordpressFetch("GET", Config.clubsApi);
};

export const fetchOptions = (): Promise<any> => {
    return wordpressFetch("GET", Config.optionsAPI).then(
        ({ acf }: any): any => acf
    );
};

export const fetchPage = async (
    pagePath: string = "/",
    pageQuery: string
): Promise<any> => {
    const queries = qs.parse(pageQuery);
    if (
        queries.preview &&
        (queries.preview_id || queries.p || queries.page_id)
    ) {
        return wordpressFetch(
            "GET",
            `${Config.dynamicPageAPI}?preview=true&page_id=${
                queries.preview_id || queries.p || queries.page_id
            }`
        );
    } else {
        if (pagePath.startsWith("/product/")) {
            return wordpressFetch("GET", `${Config.pageAPI}?path=${pagePath}`);
        } else {
            const fetchResponse = await wordpressFetch(
                "GET",
                `${Config.pageAPI}?path=${pagePath}`
            );
            if (fetchResponse.post_status === "private") {
                return wordpressFetch(
                    "GET",
                    `${Config.dynamicPageAPI}?path=${pagePath}`
                );
            }
            return fetchResponse;
        }
    }
};

export const addSubscriber = (name: string, email: string): Promise<any> => {
    return fetch(Config.cmAPI, {
        method: "POST",
        headers: {
            "Content-Type": "application/x-www-form-urlencoded",
        },
        body: `name=${encodeURIComponent(name)}&email=${encodeURIComponent(
            email
        )}`,
    })
        .then((res: any): any => res.json())
        .then((res: any): any => {
            if (res && res.success) {
                return res;
            }
            const error = { message: "Something went wrong" };
            throw error;
        });
};

const fetchDataWithHeaders = (url: string): Promise<any> => {
    return wordpressFetch("GET", url, null, null, true).then(
        async (res: any): Promise<any> => {
            return res;
            // let headers = {};
            // for (let [key, value] of res.headers.entries()) {
            //     headers[key] = value;
            // }
            // return { data: await res.json(), headers };
        }
    );
};

export const fetchPosts = ({
    endpoint,
    postType,
    featured,
    perPage = 9,
    page = 1,
    orderby,
    filterKey,
    filterValue,
    exclude,
    metaKey,
    metaValue,
    metaCompare,
    fields,
    queryParms,
    offset = 0,
}: {
    endpoint?: string;
    postType?: string;
    featured?: boolean;
    perPage?: number;
    page?: number;
    orderby?: string;
    filterKey?: string;
    filterValue?: string;
    exclude?: string;
    metaKey?: string;
    metaValue?: string;
    metaCompare?: string;
    fields?: string[];
    queryParms?: {
        page_num?: any;
    };
    offset?: number;
}): Promise<any> => {
    let params: any = {
        // orderby: "menu_order",
        // order: "asc",
        per_page: perPage,
        page: queryParms && queryParms.page_num ? queryParms.page_num : page,
    };

    if (orderby) {
        params = {
            orderby: orderby,
            per_page: perPage,
            page:
                queryParms && queryParms.page_num ? queryParms.page_num : page,
        };
    }

    if (featured !== undefined && featured !== null) {
        params.meta_key = "meta_0_featured";
        params.meta_value = featured ? 1 : 0;
    }

    if (metaKey !== undefined && metaValue !== undefined) {
        params.meta_key = metaKey;
        params.meta_value = metaValue;
    }
    // if (postType === "news") {
    //     params.orderby = "date";
    //     params.order = "desc";
    // }
    // if (postType === "profiles") {
    //     params.orderby = "title";
    //     params.order = "asc";
    // }
    // if (postType === "research-centres") {
    //     params.parent = "0";
    // }

    if (metaKey !== undefined) {
        params.meta_key = metaKey;
        params.meta_compare = metaCompare || "=";
    }

    if (fields) {
        params["_fields"] = fields.join(",");
    }

    if (filterKey && filterValue) {
        params[filterKey] = filterValue;
    }

    if (exclude) {
        params.exclude = exclude;
    }

    if (offset) {
        params.offset = offset;
    }

    if (queryParms) {
        params = { ...params, ...queryParms };
    }

    const urlParams = keyValueToURLParams(params);

    let url;
    if (endpoint) {
        url = `${Config.wordpressHost}${endpoint}?${urlParams}`;
    } else {
        url = `${Config.genericWordpressAPI}/${postType}?${urlParams}`;
    }

    return fetchDataWithHeaders(url);
};

export const fetchCategories = (categorySlug: string): Promise<any> => {
    return fetch(
        `${Config.genericWordpressAPI}/${categorySlug}?_fields=id,name,slug,count&per_page=99`
    ).then((res: any): any => res.json());
};

export const fetchEventHostType = (eventHostId: string): Promise<any> => {
    return fetch(
        `${Config.genericWordpressAPI}/event_host/${eventHostId}`
    ).then((res: any): any => res.json());
};

export const search = ({
    term,
    type,
    page = 1,
    perPage,
    fullMode,
}: {
    term: string;
    type?: string;
    page?: number;
    perPage: number;
    fullMode: boolean;
}): Promise<any> => {
    return fetchDataWithHeaders(
        `${Config.searchAPI}?s=${encodeURIComponent(term || "")}${
            fullMode ? "&mode=full" : "&mode=slim"
        }${perPage ? `&per_page=${perPage}` : ""}&page=${page}${
            type ? `&type=${type}` : ""
        }`
    );
};

export const getCategoriesForPost = async (
    postType: string,
    id: string | number
): Promise<any> => {
    const cat = getCategoryForPostType(postType);
    const res = await fetch(
        `${Config.genericWordpressAPI}/${postType}/${id}?_fields=${cat},id`
    );
    const data = await res.json();
    const cats = data[cat];
    if (cats) {
        return cats;
    }
    return [];
};

export const getBreadcrumbsForSinglePostType = (
    postType: string,
    parents?: Array<BreadcrumbLinkType>
): Promise<BreadcrumbLinkType[]> => {
    return fetchPosts({
        postType: "pages",
        perPage: 1,
        metaKey: "post_type",
        metaValue: getPostTypeAPIName(postType),
        fields: ["title", "id", "link", "breadcrumbs"],
    }).then(({ data }): Array<any> => {
        const [listingPage] = data;
        if (!listingPage) {
            return Config.defaultBreadcrumbs;
        }
        const listingPageCrumb = {
            id: listingPage.id,
            title: listingPage.title,
            url: listingPage.link,
        };
        if (!listingPage.breadcrumbs) {
            return [listingPageCrumb];
        }
        const fetchedCrumbs: Array<BreadcrumbLinkType> = [
            ...listingPage.breadcrumbs,
            listingPageCrumb,
        ];
        if (parents) {
            fetchedCrumbs.push(...parents);
        }
        return fetchedCrumbs;
    });
};

export const uploadWebformFile = (formData: any): Promise<any> => {
    let url = `${Config.formAPI}/file-upload`;

    const headers = new Headers();
    // headers.append("Content-Type", "multipart/form-data");

    return wordpressFetch("POST", url, headers, formData, null, true);
};

export const getEventTerms = (
    cid: string,
    eid: string,
    code: string
): Promise<any> => {
    let url = `${Config.pageApi}/event-tnc?cid=${cid}&eid=${eid}&code=${code}`;
    const headers = new Headers();
    return wordpressFetch("GET", url, headers, null, null, true);
};

export const agreeEventTerms = (
    cid: string,
    eid: string,
    code: string
): Promise<any> => {
    let url = `${Config.pageApi}/event-tnc/agree?cid=${cid}&eid=${eid}&code=${code}`;
    const headers = new Headers();
    return wordpressFetch("POST", url, headers, null, null, true);
};

const api = {
    fetchOptions,
    fetchPage,
    addSubscriber,
    fetchTagsCounter,
    fetchContentTags,
    fetchEvents,
    fetchClubs,
    fetchPosts,
    search,
    getCategoriesForPost,
    getBreadcrumbsForSinglePostType,
    uploadWebformFile,
    getEventTerms,
    agreeEventTerms,
    fetchEventHostType,
};

export default api;
